import { type FC, useState } from 'react'
import { Alert, Collapse } from 'react-bootstrap'
import classNames from 'classnames'
import { type CustomWorkflowDetail, type CustomWorkflowRun, type CustomWorkflowSummary } from '../../store/customWorkflow/customWorkflows'
import { Spinner } from '../Spinner/Spinner'
import { Button } from '../Button/Button'
import { CategoryIcon } from './tags/CategoryIcon'
import { parseWorkflowTags } from './workflowTags'
import { WorkflowRunStatusBadge } from './WorkflowRunStatusBadge'
import { RunContextDetail } from './runContext/RunContextDetail'
import { RunInputSummary } from './runContext/RunInputSummary'
import { hintsFromSchema, renderWorkflowOutputs } from './workflowOutputs'
import { CopyOutputButton } from './CopyOutputButton'
import { formatRunTimestamp } from './formatRunTimestamp'
import { formatRunDuration } from './formatRunDuration'
import styles from './CustomWorkflows.module.scss'

interface RunResultViewProps {
  accountId: string
  workflowId: string
  runId: string
  workflowName: string
  status: string
  createdAt: number | null
  finishedAt: number | null
  elapsedTime: number | null
  workflow?: CustomWorkflowDetail | CustomWorkflowSummary
  detail?: CustomWorkflowRun
  isLoading: boolean
  isError: boolean
}

const hasOutputSchema = (workflow?: CustomWorkflowDetail | CustomWorkflowSummary): workflow is CustomWorkflowDetail =>
  workflow !== undefined && 'outputSchema' in workflow

export const RunResultView: FC<RunResultViewProps> = ({
  accountId,
  workflowId,
  runId,
  workflowName,
  status,
  createdAt,
  finishedAt,
  elapsedTime,
  workflow,
  detail,
  isLoading,
  isError,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const category = workflow ? parseWorkflowTags(workflow).category : null
  const contextCount = detail?.context?.length ?? 0
  const inputCount = detail?.inputValues?.length ?? 0
  const fileCount = detail?.files?.length ?? 0
  const hasDetails = contextCount + inputCount + fileCount > 0
  const summary = [
    inputCount > 0 ? `${inputCount} input${inputCount === 1 ? '' : 's'}` : null,
    fileCount > 0 ? `${fileCount} file${fileCount === 1 ? '' : 's'}` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')

  return (
    <div className='d-flex flex-column zocks-gap-3'>
      <div className='d-flex align-items-center zocks-gap-2 flex-wrap'>
        <CategoryIcon category={category} />
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          <span className='paragraph-medium medium'>{workflowName}</span>
          <WorkflowRunStatusBadge status={status} />
        </div>
      </div>
      <div className='paragraph-small zocks-text-gray-600'>
        Started {formatRunTimestamp(createdAt)}
        {finishedAt ? ` · Finished ${formatRunTimestamp(finishedAt)}` : ''}
        {elapsedTime !== null ? ` · ${formatRunDuration(elapsedTime)}` : ''}
      </div>

      {hasDetails ? (
        <div>
          <Button
            type='button'
            variant='secondaryGhostButton'
            className='px-2 py-1 rounded-2'
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            <span className='paragraph-extra-small medium d-inline-flex align-items-center gap-1'>
              <i className={classNames('bi', detailsOpen ? 'bi-chevron-down' : 'bi-chevron-right')} aria-hidden='true' />
              Run details{summary ? ` · ${summary}` : ''}
            </span>
          </Button>
          <Collapse in={detailsOpen}>
            <div>
              {detailsOpen ? (
                <div className='pt-2'>
                  {contextCount > 0 ? <RunContextDetail context={detail?.context} accountId={accountId} /> : null}
                  {inputCount + fileCount > 0 ? (
                    <RunInputSummary
                      inputValues={detail?.inputValues ?? []}
                      files={detail?.files ?? []}
                      accountId={accountId}
                      workflowId={workflowId}
                      runId={runId}
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </Collapse>
        </div>
      ) : null}

      {isLoading ? (
        <div className='d-flex align-items-center justify-content-center zocks-gap-2 py-5'>
          <Spinner />
          <span className='paragraph-small shimmering-loader-text'>Loading result…</span>
        </div>
      ) : null}
      {isError ? (
        <Alert variant='warning' className='mb-0'>
          Could not load the run output.
        </Alert>
      ) : null}
      {!isLoading && !isError && detail ? (
        detail.error ? (
          <Alert variant='danger' className='mb-0'>
            <div className='d-flex justify-content-end mb-2'>
              <CopyOutputButton text={detail.error} />
            </div>
            <pre className={classNames('mb-0 paragraph-small', styles.outputText)}>{detail.error}</pre>
          </Alert>
        ) : (
          renderWorkflowOutputs(detail.outputs, hasOutputSchema(workflow) ? hintsFromSchema(workflow.outputSchema) : undefined, {
            accountId,
            workflowId,
            runId,
          })
        )
      ) : null}
    </div>
  )
}
